import React from "react";
import { NavLink } from "react-router-dom";
import { X } from "lucide-react";
import { MENU } from "./menu-items";
import { Logo } from "@/components/Logo";

/**
 * Sidebar renders in two modes:
 *   - Desktop (lg+): fixed left rail, always visible
 *   - Mobile (<lg): slide-over drawer, controlled by `mobileOpen` + `onClose`
 */
export const Sidebar = ({ mobileOpen = false, onClose = () => {} }) => {
  const railContent = (
    <>
      <div className="flex h-16 items-center justify-between border-b border-border px-6">
        <Logo to="/dashboard" size={26} testid="logo-link" />
        <button
          onClick={onClose}
          className="lg:hidden iso-btn-ghost !p-1.5"
          aria-label="Close menu"
          data-testid="sidebar-close-btn"
        >
          <X className="size-4" />
        </button>
      </div>
      <nav className="flex-1 overflow-y-auto custom-scrollbar px-3 py-5">
        {MENU.map((item, i) => {
          if (item.label) {
            return (
              <div key={`lbl-${i}`} className="iso-label mt-5 first:mt-0 px-3">
                {item.label}
              </div>
            );
          }
          const Icon = item.icon;
          return (
            <NavLink
              key={item.href}
              to={item.href}
              onClick={onClose}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all ${
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                }`
              }
            >
              <Icon className="size-[18px]" />
              <span>{item.title}</span>
            </NavLink>
          );
        })}
      </nav>
      <div className="border-t border-border p-4">
        <div className="rounded-xl border border-border bg-gradient-to-br from-secondary to-secondary-dark p-4 text-white">
          <div className="font-lexend text-xs font-semibold uppercase tracking-wider opacity-80">
            Pro Tip
          </div>
          <p className="mt-1.5 text-xs leading-relaxed text-white/90">
            Always cross-reference the model's top edge with line movement before placing a bet.
          </p>
        </div>
      </div>
    </>
  );

  return (
    <>
      {/* Desktop rail */}
      <aside className="hidden lg:flex w-[260px] shrink-0 flex-col border-r border-border bg-gray-0" data-testid="sidebar-desktop">
        {railContent}
      </aside>

      {/* Mobile drawer + backdrop */}
      <div
        className={`lg:hidden fixed inset-0 z-40 transition-opacity duration-200 ${mobileOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
        aria-hidden={!mobileOpen}
        data-testid="sidebar-mobile-backdrop"
        onClick={onClose}
      >
        <div className="absolute inset-0 bg-black/50 backdrop-blur-[1px]" />
      </div>
      <aside
        className={`lg:hidden fixed inset-y-0 left-0 z-50 w-[280px] max-w-[85vw] flex flex-col border-r border-border bg-gray-0 shadow-2xl transform transition-transform duration-250 ease-out ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}
        role="dialog"
        aria-modal="true"
        data-testid="sidebar-mobile"
      >
        {railContent}
      </aside>
    </>
  );
};
